// src/crypto/vault.js — the caches, unreadable without the person.
//
// ══ 🟥 WHAT THIS CAN AND CANNOT BE, SAID FIRST ═══════════════════════════════
//
//    At-rest encryption in a browser is only real when there is a secret the
//    DISK DOES NOT HOLD. If the key sits in IndexedDB beside the ciphertext, an
//    attacker with the laptop reads the key and then the data — the encryption
//    has cost them one extra step and bought nothing. Shipping that and calling
//    it "encrypted at rest" would be worse than shipping nothing, because it
//    would end the conversation about the risk while leaving the risk in place.
//
//    So: the vault exists ONLY once the person has enrolled a secret (a PIN, or
//    a WebAuthn platform authenticator that supports PRF). Not enrolled means no
//    vault, no ciphertext, and the 24-hour wipe in plaintextGuard is the whole
//    protection — which is honest, and is what every other web messenger does.
//
// ══ 🟢 THE SHAPE ════════════════════════════════════════════════════════════
//
//    A random DEK (AES-GCM 256) encrypts the caches. The DEK is never stored in
//    the clear: it is WRAPPED by a KEK derived from the person's secret, and the
//    wrapped copy is what lives on disk. The DEK itself exists only in memory,
//    and is dropped on idle.
//
// 🟥 WRAPPED, NOT DERIVED-AND-USED-DIRECTLY. If the caches were encrypted
//    straight under a PIN-derived key, changing the PIN would mean re-encrypting
//    every blob this browser holds — so in practice nobody would ever change it.
//    One indirection makes a PIN change a single re-wrap of 32 bytes.
//
// 🟥 AND SUCCESSFUL UNWRAP IS THE VERIFICATION. There is no stored hash of the
//    PIN to compare against, because a stored hash is one more thing to crack
//    offline and it proves nothing the unwrap does not. A wrong PIN produces a
//    wrong KEK, AES-GCM's tag fails, and that IS the answer.

const DB = 'allo_web_vault';
const STORE = 'keys';
const REC = 'wrapped_v1';

/* 🟢 OWASP's current figure for PBKDF2-HMAC-SHA256 (Password Storage Cheat
   Sheet). It is deliberately slow: this runs once per unlock, not per read. */
const PBKDF2_ITERS = 600_000;
const KDF_HASH = 'SHA-256';

/* 🟥 TWELVE BYTES, RANDOM, PER ENCRYPTION — NEVER A COUNTER. Reusing an IV under
   one AES-GCM key does not merely weaken it: it leaks the XOR of the two
   plaintexts and lets an attacker forge tags. A counter looks tidier right up
   until a page reload restarts it at zero over the same key, which is exactly
   what this application does all day. */
const IV_BYTES = 12;

const subtle = () => window.crypto.subtle;
const rand = (n) => window.crypto.getRandomValues(new Uint8Array(n));

/* ── the one record on disk ────────────────────────────────────────────── */
function idb() {
  return new Promise((res, rej) => {
    let r;
    try { r = indexedDB.open(DB, 1); } catch (e) { rej(e); return; }
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
async function recGet() {
  const d = await idb();
  return new Promise((res) => {
    const t = d.transaction(STORE, 'readonly').objectStore(STORE).get(REC);
    t.onsuccess = () => res(t.result ?? null); t.onerror = () => res(null);
  });
}
async function recPut(v) {
  const d = await idb();
  return new Promise((res, rej) => {
    const t = d.transaction(STORE, 'readwrite').objectStore(STORE).put(v, REC);
    t.onsuccess = () => res(); t.onerror = () => rej(t.error);
  });
}

/* ── in-memory state: the DEK and nothing else ─────────────────────────── */
let _dek = null;             // CryptoKey | null — null means locked or absent
let _enrolled = null;        // cached boolean, so a read does not hit IndexedDB

export class VaultLocked extends Error {
  constructor() { super('vault is locked'); this.name = 'VaultLocked'; }
}

/** Has this browser enrolled a secret? */
export async function isEnrolled() {
  if (_enrolled !== null) return _enrolled;
  _enrolled = !!(await recGet());
  return _enrolled;
}

/** Is the DEK in memory right now? */
export function isUnlocked() { return !!_dek; }

/**
 * 🟥 THE QUESTION THE STORES MUST ASK, AND IT IS NOT `isEnrolled`. A store needs
 *    to know whether it may WRITE SEALED BYTES, which is true only when the key
 *    is actually in hand. Asking "is there a vault" and then sealing with a null
 *    key is how a cache ends up holding records nothing can ever open.
 */
export function canSeal() { return !!_dek; }

/** Forget the key. The ciphertext stays; nothing can read it until unlock. */
export function lock() { _dek = null; }

/* ── key derivation ────────────────────────────────────────────────────── */
async function kekFromPin(pin, salt) {
  const base = await subtle().importKey('raw', new TextEncoder().encode(String(pin)), 'PBKDF2', false, ['deriveKey']);
  return subtle().deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERS, hash: KDF_HASH },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['wrapKey', 'unwrapKey'],
  );
}

/**
 * Create the vault, or re-wrap it under a new PIN.
 *
 * 🟥 THE DEK IS GENERATED ONCE AND KEPT ACROSS PIN CHANGES. Minting a new one on
 *    every enrolment would silently orphan every record already sealed — the
 *    data would still be there and nothing would ever open it again, which is
 *    data loss that reports itself as an empty cache.
 */
export async function enrollWithPin(pin) {
  if (!pin || String(pin).length < 4) throw new Error('pin too short');
  const dek = _dek || await subtle().generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
  const salt = rand(16);
  const iv = rand(IV_BYTES);
  const kek = await kekFromPin(pin, salt);
  const wrapped = await subtle().wrapKey('raw', dek, kek, { name: 'AES-GCM', iv });
  await recPut({ v: 1, kdf: 'PBKDF2', hash: KDF_HASH, iters: PBKDF2_ITERS, salt, iv, wrapped });
  _dek = dek;
  _enrolled = true;
}

/**
 * Put the key back in memory. Returns false for a wrong PIN, throws for a
 * vault that is not there at all — those are different problems and a caller
 * that cannot tell them apart shows the wrong screen for one of them.
 */
export async function unlockWithPin(pin) {
  const rec = await recGet();
  if (!rec) throw new Error('no vault on this browser');
  const kek = await kekFromPin(pin, rec.salt);
  try {
    _dek = await subtle().unwrapKey(
      'raw', rec.wrapped, kek, { name: 'AES-GCM', iv: rec.iv },
      { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt'],
    );
    return true;
  } catch {
    /* 🟢 the tag failed — that is the wrong PIN, and it is the ONLY check.
       There is no stored hash to disagree with this one. */
    _dek = null;
    return false;
  }
}

/* ── sealing ───────────────────────────────────────────────────────────── */

/**
 * 🟥 A FRESH IV EVERY SINGLE TIME, GENERATED HERE SO NO CALLER CAN SUPPLY ONE.
 *    Every reuse bug in AES-GCM starts with an IV that came from somewhere else.
 */
export async function seal(bytes) {
  if (!_dek) throw new VaultLocked();
  const iv = rand(IV_BYTES);
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv }, _dek, bytes);
  return { enc: 1, iv, ct };
}

/**
 * 🟥 A FAILED OPEN IS NOT AN EMPTY CACHE. Swallowing it into `null` turns a
 *    locked vault — or, worse, a corrupted record — into "there is nothing
 *    here", which reads to every layer above as data loss and sets off a
 *    re-sync that will fail the same way next time. It throws.
 */
export async function open(rec) {
  if (!rec || rec.enc !== 1) throw new Error('not a sealed record');
  if (!_dek) throw new VaultLocked();
  return subtle().decrypt({ name: 'AES-GCM', iv: rec.iv }, _dek, rec.ct);
}

/* ── the two shapes the caches actually hold ───────────────────────────── */
export async function sealBlob(blob) {
  const buf = await blob.arrayBuffer();
  const r = await seal(buf);
  r.type = blob.type || '';
  return r;
}
export async function openBlob(rec) {
  const buf = await open(rec);
  return new Blob([buf], { type: rec.type || '' });
}
export async function sealJson(value) {
  return seal(new TextEncoder().encode(JSON.stringify(value)));
}
export async function openJson(rec) {
  return JSON.parse(new TextDecoder().decode(await open(rec)));
}

/**
 * Delete the wrapped key, so this browser has no vault any more.
 *
 * 🟥 THE CALLER MUST HAVE FREED THE DATA FIRST. Deleting this record while sealed
 *    records remain destroys the only key that could ever open them — the bytes
 *    stay on disk looking like data and are gone for good. removeCode() in
 *    services/vaultAdmin does the unsealing and calls this last, and nothing else
 *    should call it directly.
 */
export async function deleteVaultRecord() {
  const d = await idb();
  await new Promise((res) => {
    const t = d.transaction(STORE, 'readwrite').objectStore(STORE).delete(REC);
    t.onsuccess = () => res(); t.onerror = () => res();
  });
  _dek = null;
  _enrolled = false;
}

/** Remove the vault entirely — used by the full logout wipe. */
export function forgetVault() { _dek = null; _enrolled = null; }

export const _internals = { PBKDF2_ITERS, IV_BYTES, DB, REC };
